import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import PodcastSearchResult from '@/models/PodcastSearchResult'
import PublisherService from '@/services/PublisherService'
import ShowService from '@/services/ShowService'
import { usePublisherStore } from '@/stores/PublisherStore'

/**
 * The submission a visitor assembled in the hero and the combined form, carried
 * through registration and the OTP round-trip. Nothing is created until the
 * email is proven, so this is the only place it lives in the meantime — hence
 * persisted.
 */
export interface OnboardingDraft {
  name: string
  rss_url?: string
  image_url?: string
  author?: string
  publisher_name?: string
  accept_rights?: boolean
  accept_terms?: boolean
}

export const useOnboardingStore = defineStore(
  'onboarding',
  () => {
    // state
    const draft = ref<OnboardingDraft|undefined>(undefined)
    const loading = ref(false)

    // getters
    const hasDraft = computed(() => !!draft.value)
    const isSubmittable = computed(() => !!draft.value?.publisher_name
      && !!draft.value?.accept_rights
      && !!draft.value?.accept_terms)

    // actions
    const startWith = (result: PodcastSearchResult) => {
      draft.value = {
        name: result.name,
        rss_url: result.rss_url,
        image_url: result.image_url,
        author: result.author,
      }
    }

    const startWithName = (name: string) => {
      draft.value = { name }
    }

    const update = (fields: Partial<OnboardingDraft>) => {
      draft.value = { ...(draft.value ?? { name: '' }), ...fields }
    }

    const clear = () => {
      draft.value = undefined
    }

    /**
     * Runs once the OTP round-trip has produced a token. Selecting the publisher
     * is what makes ApiService send X-Publisher-Id on the show request that
     * follows.
     */
    const complete = async (): Promise<void> => {
      const current = draft.value
      if (!current?.publisher_name) {
        throw new Error('Onboarding draft is incomplete')
      }

      loading.value = true
      const publisherStore = usePublisherStore()

      try {
        // otpLogin starts a fetch it does not await; joining it here means the
        // list has settled before we add to it, instead of that response landing
        // afterwards and clearing the selection we just made.
        await publisherStore.fetch()

        // A publisher submitting a second podcast reuses the account they named
        // rather than collecting a duplicate.
        const existing = publisherStore.publishers.find(
          p => p.company?.trim().toLowerCase() === current.publisher_name?.trim().toLowerCase(),
        )

        if (existing) {
          publisherStore.switchPublisher(existing.id)
        } else {
          const publisher = await PublisherService.create({ company: current.publisher_name })
          publisherStore.publishers = [...publisherStore.publishers, publisher]
          publisherStore.switchPublisher(publisher.id)
        }

        await ShowService.create({
          name: current.name,
          rss_url: current.rss_url || undefined,
          image_url: current.image_url || undefined,
          accept_rights: !!current.accept_rights,
          accept_terms: !!current.accept_terms,
        })

        clear()
      } finally {
        loading.value = false
      }
    }

    return {
      clear,
      complete,
      draft,
      hasDraft,
      isSubmittable,
      loading,
      startWith,
      startWithName,
      update,
    }
  },
  {
    persist: import.meta.env.SSR ? false : {
      afterRestore: (context) => {
        context.store.loading = false
      },
    },
  }
)
